<template>
  <div class="mc-skins-container">
    <!-- Username Input and Submit Button -->
    <div class="mc-search-container">
      <div class="mc-search-input">
        <input type="text" v-model="username" placeholder="Enter Minecraft Username"
          class="w-full bg-transparent outline-none p-2" @keyup.enter="fetchSkin">
        <button class="search-button" @click="fetchSkin" :disabled="loading" title="Fetch Skin">
          {{ loading ? 'Loading...' : 'Fetch' }}
        </button>
      </div>

      <div class="banner-container2">
        <a :href="mciconsUrl" class="h-full w-full flex justify-end sm:justify-end" target="_blank" @click="handleBannerClick">
          <img :src="codeBanner" alt="Vault Banner" class="promo-banner">
        </a>
      </div>
    </div>

    <!-- Alert Messages -->
    <div v-if="errorMessage" class="mc-alert mc-alert-error">
      {{ errorMessage }}
    </div>
    <div v-if="successMessage" class="mc-alert mc-alert-success">
      {{ successMessage }}
    </div>

    <!-- Tabs Navigation -->
    <div v-if="playerTabs.length > 0" class="mc-tabs-navigation-wrapper">
      <button class="mc-tabs-scroll-left" v-if="canScrollLeft" @click="scrollTabsLeft">
        &lt;
      </button>

      <div class="mc-tabs-container" ref="tabsContainer">
        <div class="mc-tabs-navigation">
          <div v-for="(player, index) in playerTabs" :key="player.id" @click="activeTabIndex = index"
            class="mc-tab-item" :class="{ 'mc-tab-active': activeTabIndex === index }">
            <img :src="`https://api.mineatar.io/face/${player.raw_id}?scale=2&overlay=true`" alt="" class="mc-tab-face" />
            <span class="mc-tab-username">{{ player.username }}</span>
            <button @click.stop="removeTab(index)" class="mc-tab-close">&times;</button>
          </div>
        </div>
      </div>

      <button class="mc-tabs-scroll-right" v-if="canScrollRight" @click="scrollTabsRight">
        &gt;
      </button>

      <button v-if="playerTabs.length > 1" class="mc-clear-all-tabs" @click="showClearConfirmation = true"
        title="Clear all tabs">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="trash-icon">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
          <line x1="10" y1="11" x2="10" y2="17"></line>
          <line x1="14" y1="11" x2="14" y2="17"></line>
          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
        </svg>
      </button>
    </div>

    <!-- Tab Content -->
    <div v-if="playerTabs.length > 0" class="mc-skins-content">
      <!-- Current Active Player Tab Content -->
      <div v-if="activePlayer" class="mc-player-content">
        <div class="mc-player-header">
          <div class="mc-player-info">
            <img :src="`https://api.mineatar.io/face/${activePlayer.raw_id}?scale=8&overlay=true`" alt="Avatar"
              class="mc-player-avatar">
            <div class="mc-player-details">
              <h2 class="mc-player-username">{{ activePlayer.username }}</h2>
              <p class="mc-player-uuid">UUID: {{ formatUUID(activePlayer.id) }}</p>
            </div>
          </div>

          <div class="mc-player-actions">
            <!-- Multi-tab: show Player + Batch buttons -->
            <template v-if="playerTabs.length > 1">
              <button class="mc-download-all-btn mc-download-player-btn" @click="openDownloadOptions('current')"
                :disabled="isDownloading">
                {{ isDownloading ? 'Creating Zip...' : 'Download Player' }}
              </button>
              <button class="mc-download-all-btn" @click="openDownloadOptions('all')"
                :disabled="isDownloading">
                {{ isDownloading ? 'Creating Zip...' : 'Download Batch' }}
              </button>
            </template>

            <!-- Single tab: direct download -->
            <button v-else class="mc-download-all-btn" @click="openDownloadOptions('current')" :disabled="isDownloading">
              {{ isDownloading ? 'Creating Zip...' : 'Download All' }}
            </button>
          </div>
        </div>

        <div class="mc-skins-grid">
          <!-- Face Render -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://api.mineatar.io/face/${activePlayer.raw_id}?scale=8&overlay=true`" alt="Face">
            </div>
            <div class="mc-skin-info">
              <h3>Face Flat</h3>
              <div class="mc-skin-options">
                <button @click="downloadImage('face', activePlayer.raw_id, 100)"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Face Render (VZGE) -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://vzge.me/face/128/${activePlayer.raw_id}.png`" alt="VZGE Face"
                style="transform: scale(0.75);">
            </div>
            <div class="mc-skin-info">
              <h3>Face</h3>
              <div class="mc-skin-options">
                <button
                  @click="downloadExternalImage(`https://vzge.me/face/512/${activePlayer.raw_id}.png`, 'vzge_face')"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Head Render (Mineatar) -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://api.mineatar.io/head/${activePlayer.raw_id}?scale=8&overlay=true`" alt="Head">
            </div>
            <div class="mc-skin-info">
              <h3>Flat Head</h3>
              <div class="mc-skin-options">
                <button @click="downloadImage('head', activePlayer.raw_id, 100)"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Head Render (MCStats) -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://skins.mcstats.com/skull/${activePlayer.raw_id}`" alt="3D Head">
            </div>
            <div class="mc-skin-info">
              <h3>Head</h3>
              <div class="mc-skin-options">
                <button
                  @click="downloadExternalImage(`https://skins.mcstats.com/skull/${activePlayer.raw_id}`, 'head_3d')"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Bust Render (nmsr) -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://nmsr.nickac.dev/bust/${activePlayer.raw_id}`" alt="Bust 3D">
            </div>
            <div class="mc-skin-info">
              <h3>Bust</h3>
              <div class="mc-skin-options">
                <button @click="downloadExternalImage(`https://nmsr.nickac.dev/bust/${activePlayer.raw_id}`, 'bust_3d')"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Bust Flat Render (Minotar) -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://minotar.net/armor/bust/${activePlayer.raw_id}/1024.png`" alt="Bust Flat">
            </div>
            <div class="mc-skin-info">
              <h3>Flat Bust</h3>
              <div class="mc-skin-options">
                <button
                  @click="downloadExternalImage(`https://minotar.net/armor/bust/${activePlayer.raw_id}/1024.png`, 'bust_flat')"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Full Body Render (Mineatar) -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://api.mineatar.io/body/full/${activePlayer.raw_id}?scale=8&overlay=true`"
                alt="Full Body">
            </div>
            <div class="mc-skin-info">
              <h3>Full Body</h3>
              <div class="mc-skin-options">
                <button @click="downloadImage('body/full', activePlayer.raw_id, 27)"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Front Body Render (Mineatar) -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://api.mineatar.io/body/front/${activePlayer.raw_id}?scale=8&overlay=true`"
                alt="Front Body">
            </div>
            <div class="mc-skin-info">
              <h3>Front Body Flat</h3>
              <div class="mc-skin-options">
                <button @click="downloadImage('body/front', activePlayer.raw_id, 100)"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Front Body Render (nmsr) -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://nmsr.nickac.dev/fullbody/${activePlayer.raw_id}`" alt="Front Body 3D">
            </div>
            <div class="mc-skin-info">
              <h3>Front Body</h3>
              <div class="mc-skin-options">
                <button
                  @click="downloadExternalImage(`https://nmsr.nickac.dev/fullbody/${activePlayer.raw_id}`, 'body_front_3d')"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Back Body Render (Mineatar) -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://api.mineatar.io/body/back/${activePlayer.raw_id}?scale=8&overlay=true`"
                alt="Back Body">
            </div>
            <div class="mc-skin-info">
              <h3>Back Body Flat</h3>
              <div class="mc-skin-options">
                <button @click="downloadImage('body/back', activePlayer.raw_id, 100)"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Back Body Render (MCStats) -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://skins.mcstats.com/body/back/${activePlayer.raw_id}`" alt="Back Body 3D">
            </div>
            <div class="mc-skin-info">
              <h3>Back Body</h3>
              <div class="mc-skin-options">
                <button
                  @click="downloadExternalImage(`https://skins.mcstats.com/body/back/${activePlayer.raw_id}`, 'body_back_3d')"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Left Body Render -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://api.mineatar.io/body/left/${activePlayer.raw_id}?scale=8&overlay=true`"
                alt="Left Body">
            </div>
            <div class="mc-skin-info">
              <h3>Left Body Flat</h3>
              <div class="mc-skin-options">
                <button @click="downloadImage('body/left', activePlayer.raw_id, 100)"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Right Body Render -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://api.mineatar.io/body/right/${activePlayer.raw_id}?scale=8&overlay=true`"
                alt="Right Body">
            </div>
            <div class="mc-skin-info">
              <h3>Right Body Flat</h3>
              <div class="mc-skin-options">
                <button @click="downloadImage('body/right', activePlayer.raw_id, 100)"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Side Body Render (nmsr) -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://nmsr.nickac.dev/fullbodyiso/${activePlayer.raw_id}`" alt="Side Body 3D">
            </div>
            <div class="mc-skin-info">
              <h3>Side Body</h3>
              <div class="mc-skin-options">
                <button
                  @click="downloadExternalImage(`https://nmsr.nickac.dev/fullbodyiso/${activePlayer.raw_id}`, 'body_side_3d')"
                  class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>

          <!-- Raw Skin Render -->
          <div class="mc-skin-item">
            <div class="mc-skin-preview">
              <img :src="`https://api.mineatar.io/skin/${activePlayer.raw_id}`" alt="Raw Skin" class="mc-raw-skin">
            </div>
            <div class="mc-skin-info">
              <h3>Raw Skin</h3>
              <div class="mc-skin-options">
                <button @click="downloadImage('skin', activePlayer.raw_id)" class="mc-download-btn">Download</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Credits Section -->
    <div v-if="playerTabs.length > 0" class="mc-credits">
      <p>Data and renders provided by:
        <a href="https://playerdb.co/" target="_blank" rel="noopener noreferrer">PlayerDB</a> |
        <a href="https://mineatar.io/" target="_blank" rel="noopener noreferrer">Mineatar</a> |
        <a href="https://skins.mcstats.com/" target="_blank" rel="noopener noreferrer">MCStats</a> |
        <a href="https://minotar.net/" target="_blank" rel="noopener noreferrer">Minotar</a> |
        <a href="https://vzge.me/" target="_blank" rel="noopener noreferrer">VZGE</a> |
        <a href="https://nmsr.nickac.dev/" target="_blank" rel="noopener noreferrer">NMSR</a>
      </p>
    </div>

    <!-- Empty State -->
    <div v-if="playerTabs.length === 0" class="mc-empty-state">
      <p class="mc-empty-state-message">
        Type a valid Minecraft username above and click "Fetch" to view and download skin renders.
      </p>
    </div>

    <!-- Clear All Confirmation Modal -->
    <Transition name="fade">
      <div v-if="showClearConfirmation" class="mc-modal-overlay" @click.self="showClearConfirmation = false">
        <Transition name="fade-scale">
          <div v-if="showClearConfirmation" class="mc-confirm-modal">
            <div class="mc-confirm-modal-header">
              <h3>Clear All Tabs</h3>
            </div>
            <div class="mc-confirm-modal-content">
              <p>Are you sure you want to clear all player tabs? This action cannot be undone.</p>
              <div class="mc-confirm-modal-actions">
                <button @click="showClearConfirmation = false" class="mc-cancel-modal-btn">Cancel</button>
                <button @click="clearAllTabs" class="mc-confirm-modal-btn">Clear All</button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>

    <!-- Download Progress Modal -->
    <Transition name="fade">
      <div v-if="showProgressModal" class="mc-progress-modal-overlay" @click.self="cancelDownload">
        <Transition name="fade-scale">
          <div v-if="showProgressModal" class="mc-progress-modal">
            <div class="mc-progress-modal-header">
              <h3>{{ progressTitle }}</h3>
            </div>
            <div class="mc-progress-modal-content">
              <div class="mc-progress-info">
                <div class="mc-progress-percentage">{{ progressPercentage }}%</div>
              </div>
              <div class="mc-progress-bar-container">
                <div class="mc-progress-bar" :style="{ width: `${progressPercentage}%` }"></div>
              </div>
              <button @click="cancelDownload" class="mc-cancel-button">Cancel</button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>

    <!-- Download Options Modal -->
    <Transition name="fade">
      <div v-if="showDownloadOptions" class="mc-modal-overlay" @click.self="showDownloadOptions = false">
        <Transition name="fade-scale">
          <div v-if="showDownloadOptions" class="mc-download-options-modal">
            <div class="mc-download-options-header">
              <h3>Download Options</h3>
            </div>
            <div class="mc-download-options-content">
              <div class="mc-render-options no-scope">
                <label class="mc-checkbox-label select-all-checkbox">
                  <input type="checkbox" v-model="renderOptions.selectAll" @change="handleSelectAllChange">
                  <span class="checkbox-custom"></span>
                  Select All
                </label>

                <div class="mc-render-checkboxes">
                  <label class="mc-checkbox-label" v-for="(option, key) in renderOptions.renders" :key="key">
                    <input type="checkbox" v-model="option.selected">
                    <span class="checkbox-custom"></span>
                    {{ option.label }}
                  </label>
                </div>
              </div>
            </div>
            <div class="mc-download-options-actions">
              <button @click="showDownloadOptions = false" class="mc-cancel-modal-btn">Cancel</button>
              <button @click="startBatchDownload" class="mc-confirm-modal-btn"
                :disabled="!hasSelectedRenders">Download</button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted, nextTick, watch, onUnmounted } from 'vue';
import codeBanner from '@/assets/banners/code-banner.png';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const mciconsUrl = getCurrentInstance().appContext.config.globalProperties.$mciconsUrl;

const handleBannerClick = (event) => {
  if (Math.random() < 0.5) {
    event.preventDefault();
    window.open('https://app.ccleaf.com?ref=apollo', '_blank');
  }
};

// State
const username = ref('');
const playerTabs = ref([]);
const activeTabIndex = ref(0);
const errorMessage = ref('');
const successMessage = ref('');
const loading = ref(false);
const isDownloading = ref(false);

// Progress modal state
const showProgressModal = ref(false);
const progressPercentage = ref(0);
const progressTitle = ref('');
const cancelRequested = ref(false);

// Tabs scrolling functionality
const tabsContainer = ref(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const showClearConfirmation = ref(false);

// Add new refs for download options modal
const showDownloadOptions = ref(false);
const downloadScope = ref('current');
const renderOptions = ref({
  selectAll: true,
  renders: {
    faceFlat: { label: 'Face Flat', selected: true },
    face: { label: 'Face', selected: true },
    flatHead: { label: 'Flat Head', selected: true },
    head3d: { label: 'Head 3D', selected: true },
    bust3d: { label: 'Bust 3D', selected: true },
    bustFlat: { label: 'Flat Bust', selected: true },
    fullBody: { label: 'Full Body', selected: true },
    frontBodyFlat: { label: 'Front Body Flat', selected: true },
    frontBody3d: { label: 'Front Body 3D', selected: true },
    backBodyFlat: { label: 'Back Body Flat', selected: true },
    backBody3d: { label: 'Back Body 3D', selected: true },
    leftBodyFlat: { label: 'Left Body Flat', selected: true },
    rightBodyFlat: { label: 'Right Body Flat', selected: true },
    sideBody3d: { label: 'Side Body 3D', selected: true },
    rawSkin: { label: 'Raw Skin', selected: true }
  }
});

// Add a ref to track if the selectAll change is coming from user action or programmatic change
const isUserAction = ref(false);

const checkTabsOverflow = () => {
  if (!tabsContainer.value) return;

  const container = tabsContainer.value;
  const isOverflowing = container.scrollWidth > container.clientWidth;

  canScrollRight.value = isOverflowing && container.scrollLeft < container.scrollWidth - container.clientWidth;
  canScrollLeft.value = isOverflowing && container.scrollLeft > 0;
};

const scrollTabsLeft = () => {
  if (!tabsContainer.value) return;

  const container = tabsContainer.value;
  container.scrollBy({ left: -200, behavior: 'smooth' });

  setTimeout(() => {
    checkTabsOverflow();
  }, 300);
};

const scrollTabsRight = () => {
  if (!tabsContainer.value) return;

  const container = tabsContainer.value;
  container.scrollBy({ left: 200, behavior: 'smooth' });

  setTimeout(() => {
    checkTabsOverflow();
  }, 300);
};

// Update scroll buttons when tabs change or when resizing
watch(playerTabs, async () => {
  await nextTick();
  checkTabsOverflow();

  // When a new tab is added, scroll to make it visible
  if (activeTabIndex.value === playerTabs.value.length - 1) {
    scrollToActiveTab();
  }
}, { deep: true });

// Also scroll to active tab when activeTabIndex changes
watch(activeTabIndex, () => {
  nextTick(() => {
    scrollToActiveTab();
  });
});

// Scroll to make active tab visible
const scrollToActiveTab = () => {
  if (!tabsContainer.value) return;

  const container = tabsContainer.value;
  const activeTab = container.querySelector('.mc-tab-active');

  if (!activeTab) return;

  const tabRect = activeTab.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  // If tab is outside view, scroll to it
  if (tabRect.left < containerRect.left) {
    // Tab is to the left of view
    container.scrollBy({
      left: tabRect.left - containerRect.left - 10,
      behavior: 'smooth'
    });
  } else if (tabRect.right > containerRect.right) {
    // Tab is to the right of view
    container.scrollBy({
      left: tabRect.right - containerRect.right + 10,
      behavior: 'smooth'
    });
  }

  // Check scroll buttons state after scroll
  setTimeout(checkTabsOverflow, 300);
};

// Load saved players from localStorage
onMounted(() => {
  loadSavedPlayers();

  // Check if tabs need scroll buttons after initial load
  nextTick(() => {
    checkTabsOverflow();
  });

  // Add resize listener to check if scroll buttons needed on resize
  window.addEventListener('resize', checkTabsOverflow);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkTabsOverflow);
});

// Load saved players from localStorage
const loadSavedPlayers = () => {
  try {
    const savedPlayers = localStorage.getItem('skinsSaved');
    if (savedPlayers) {
      const parsedPlayers = JSON.parse(savedPlayers);

      // Fetch and load each saved player
      for (const player of parsedPlayers) {
        fetchPlayerById(player.username, player.id);
      }
    }
  } catch (error) {
    console.error('Error loading saved players:', error);
  }
};

// Save players to localStorage
const savePlayers = () => {
  try {
    // Only save username and UUID to localStorage
    const playersToSave = playerTabs.value.map(player => ({
      username: player.username,
      id: player.id
    }));

    localStorage.setItem('skinsSaved', JSON.stringify(playersToSave));
  } catch (error) {
    console.error('Error saving players:', error);
  }
};

// Fetch player by username and UUID (used when loading from localStorage)
const fetchPlayerById = async (playerUsername, playerId) => {
  try {
    // Create player object with the saved data
    const existingTabIndex = playerTabs.value.findIndex(player => player.id === playerId);

    if (existingTabIndex === -1) {
      // The UUID format without dashes
      const rawId = playerId.replace(/-/g, '');

      // Add new tab with basic info
      playerTabs.value.push({
        username: playerUsername,
        id: playerId,
        raw_id: rawId,
        avatar: `https://api.mineatar.io/face/${rawId}?scale=8&overlay=true`
      });

      // Save to localStorage
      savePlayers();
    }
  } catch (error) {
    console.error('Error fetching player by ID:', error);
  }
};

// Computed properties
const activePlayer = computed(() => {
  if (playerTabs.value.length === 0 || activeTabIndex.value >= playerTabs.value.length) {
    return null;
  }
  return playerTabs.value[activeTabIndex.value];
});

// Computed property to check if any renders are selected
const hasSelectedRenders = computed(() => {
  return Object.values(renderOptions.value.renders).some(render => render.selected);
});

// Computed property to check if all renders are selected
const allRendersSelected = computed(() => {
  return Object.values(renderOptions.value.renders).every(render => render.selected);
});

// Watch individual renders to update selectAll checkbox
watch(() => Object.values(renderOptions.value.renders).map(render => render.selected), () => {
  // Only update selectAll if this isn't a result of a direct user action on the selectAll checkbox
  if (!isUserAction.value) {
    renderOptions.value.selectAll = allRendersSelected.value;
  }
}, { deep: true });

// Function to handle selectAll checkbox changes
const handleSelectAllChange = (event) => {
  isUserAction.value = true;
  const checked = event.target.checked;

  // Apply the checked state to all individual checkboxes
  // When "Select All" is checked, all boxes will be checked
  // When "Select All" is unchecked, all boxes will be unchecked
  Object.values(renderOptions.value.renders).forEach(render => {
    render.selected = checked;
  });

  isUserAction.value = false;
};

// Function to start batch download
const startBatchDownload = async () => {
  showDownloadOptions.value = false;

  if (downloadScope.value === 'current') {
    await downloadAllImages();
  } else {
    await downloadAllTabs();
  }
};

// API call to fetch player data
const fetchSkin = async () => {
  if (!username.value.trim()) {
    errorMessage.value = 'Please enter a Minecraft username';
    setTimeout(() => { errorMessage.value = ''; }, 3000);
    return;
  }

  loading.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const response = await fetch(`https://playerdb.co/api/player/minecraft/${username.value.trim()}`);
    const data = await response.json();

    if (!data.success || data.code === 'minecraft.invalid_username') {
      errorMessage.value = data.message || 'Player not found';
      setTimeout(() => { errorMessage.value = ''; }, 3000);
      loading.value = false;
      return;
    }

    const playerData = data.data.player;

    // Check if player already exists in tabs
    const existingTabIndex = playerTabs.value.findIndex(player => player.id === playerData.id);

    if (existingTabIndex !== -1) {
      // Switch to existing tab
      activeTabIndex.value = existingTabIndex;
      successMessage.value = `Switched to existing player: ${playerData.username}`;
    } else {
      // Add new tab
      playerTabs.value.push({
        username: playerData.username,
        id: playerData.id,
        raw_id: playerData.raw_id,
        avatar: playerData.avatar
      });
      activeTabIndex.value = playerTabs.value.length - 1;
      successMessage.value = `Successfully fetched skin for ${playerData.username}`;

      // Save to localStorage
      savePlayers();
    }

    // Clear messages after a delay
    setTimeout(() => { successMessage.value = ''; }, 3000);
    username.value = '';
  } catch (error) {
    console.error('Error fetching player data:', error);
    errorMessage.value = 'Error connecting to the API. Please try again.';
    setTimeout(() => { errorMessage.value = ''; }, 3000);
  } finally {
    loading.value = false;
  }
};

// Format UUID with dashes for display
const formatUUID = (uuid) => {
  if (!uuid) return '';

  // If the UUID already has dashes, return it as is
  if (uuid.includes('-')) return uuid;

  // Insert dashes at the correct positions (8-4-4-4-12 format)
  return `${uuid.substring(0, 8)}-${uuid.substring(8, 12)}-${uuid.substring(12, 16)}-${uuid.substring(16, 20)}-${uuid.substring(20)}`;
};

// Remove a tab
const removeTab = (index) => {
  playerTabs.value.splice(index, 1);

  // Adjust active tab index if necessary
  if (playerTabs.value.length === 0) {
    activeTabIndex.value = 0;
  } else if (activeTabIndex.value >= playerTabs.value.length) {
    activeTabIndex.value = playerTabs.value.length - 1;
  }

  // Update localStorage
  savePlayers();
};

// Download a single image
const downloadImage = async (type, uuid, scale = 8) => {
  try {
    const response = await fetch(`https://api.mineatar.io/${type}/${uuid}?scale=${scale}&overlay=true`);
    const blob = await response.blob();

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);

    // Set filename based on type and player name
    const playerName = activePlayer.value.username;
    const typeName = type.includes('/') ? type.split('/').pop() : type;
    link.download = `${playerName}_${typeName}.png`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  } catch (error) {
    console.error('Error downloading image:', error);
    errorMessage.value = 'Failed to download image. Please try again.';
    setTimeout(() => { errorMessage.value = ''; }, 3000);
  }
};

// Cancel download
const cancelDownload = () => {
  cancelRequested.value = true;
  showProgressModal.value = false;
  isDownloading.value = false;
  progressPercentage.value = 0;
  errorMessage.value = 'Download canceled';
  setTimeout(() => { errorMessage.value = ''; }, 3000);
};

// Download all images for current player as a zip
const downloadAllImages = async () => {
  if (!activePlayer.value) return;
  isDownloading.value = true;
  showProgressModal.value = true;
  progressTitle.value = `Creating Zip for ${activePlayer.value.username}`;
  progressPercentage.value = 0;
  cancelRequested.value = false;

  try {
    const zip = new JSZip();
    const player = activePlayer.value;
    const folder = zip.folder(player.username);

    // Calculate total downloads based on selected renders
    const selectedRenders = Object.entries(renderOptions.value.renders)
      .filter(([, render]) => render.selected)
      .map(([key]) => key);

    const totalDownloads = selectedRenders.length;
    let completedDownloads = 0;

    // Download selected renders
    for (const renderType of selectedRenders) {
      if (cancelRequested.value) return;

      progressPercentage.value = Math.round((completedDownloads / totalDownloads) * 80);

      let url;
      let filename;

      switch (renderType) {
        case 'faceFlat':
          url = `https://api.mineatar.io/face/${player.raw_id}?scale=100&overlay=true`;
          filename = `${player.username}_face_flat.png`;
          break;
        case 'face':
          url = `https://vzge.me/face/512/${player.raw_id}.png`;
          filename = `${player.username}_face.png`;
          break;
        case 'flatHead':
          url = `https://api.mineatar.io/head/${player.raw_id}?scale=100&overlay=true`;
          filename = `${player.username}_head_flat.png`;
          break;
        case 'head3d':
          url = `https://skins.mcstats.com/skull/${player.raw_id}`;
          filename = `${player.username}_head_3d.png`;
          break;
        case 'bust3d':
          url = `https://nmsr.nickac.dev/bust/${player.raw_id}`;
          filename = `${player.username}_bust_3d.png`;
          break;
        case 'bustFlat':
          url = `https://minotar.net/armor/bust/${player.raw_id}/1024.png`;
          filename = `${player.username}_bust_flat.png`;
          break;
        case 'fullBody':
          url = `https://api.mineatar.io/body/full/${player.raw_id}?scale=27&overlay=true`;
          filename = `${player.username}_full_body.png`;
          break;
        case 'frontBodyFlat':
          url = `https://api.mineatar.io/body/front/${player.raw_id}?scale=100&overlay=true`;
          filename = `${player.username}_front_body_flat.png`;
          break;
        case 'frontBody3d':
          url = `https://nmsr.nickac.dev/fullbody/${player.raw_id}`;
          filename = `${player.username}_front_body_3d.png`;
          break;
        case 'backBodyFlat':
          url = `https://api.mineatar.io/body/back/${player.raw_id}?scale=100&overlay=true`;
          filename = `${player.username}_back_body_flat.png`;
          break;
        case 'backBody3d':
          url = `https://skins.mcstats.com/body/back/${player.raw_id}`;
          filename = `${player.username}_back_body_3d.png`;
          break;
        case 'leftBodyFlat':
          url = `https://api.mineatar.io/body/left/${player.raw_id}?scale=100&overlay=true`;
          filename = `${player.username}_left_body_flat.png`;
          break;
        case 'rightBodyFlat':
          url = `https://api.mineatar.io/body/right/${player.raw_id}?scale=100&overlay=true`;
          filename = `${player.username}_right_body_flat.png`;
          break;
        case 'sideBody3d':
          url = `https://nmsr.nickac.dev/fullbodyiso/${player.raw_id}`;
          filename = `${player.username}_side_body_3d.png`;
          break;
        case 'rawSkin':
          url = `https://api.mineatar.io/skin/${player.raw_id}`;
          filename = `${player.username}_raw_skin.png`;
          break;
      }

      const response = await fetch(url);
      const blob = await response.blob();
      folder.file(filename, blob);
      completedDownloads++;
    }

    // Generate and download the zip
    progressPercentage.value = 80;

    const content = await zip.generateAsync({
      type: 'blob',
      comment: 'Created using Vault Skin Viewer, visit https://mcicons.ccleaf.com/ for more!'
    }, (metadata) => {
      progressPercentage.value = 80 + Math.round(metadata.percent / 5);
    });

    if (!cancelRequested.value) {
      progressPercentage.value = 100;
      await new Promise(resolve => setTimeout(resolve, 500));
      saveAs(content, `${player.username}_minecraft_skin.zip`);
      successMessage.value = 'Zip file created and downloaded!';
      setTimeout(() => { successMessage.value = ''; }, 3000);
    }
  } catch (error) {
    console.error('Error creating zip file:', error);
    errorMessage.value = 'Failed to create zip file. Please try again.';
    setTimeout(() => { errorMessage.value = ''; }, 3000);
  } finally {
    if (!cancelRequested.value) {
      showProgressModal.value = false;
      isDownloading.value = false;
      progressPercentage.value = 0;
    }
    cancelRequested.value = false;
  }
};

// Download all images from all tabs as a zip
const downloadAllTabs = async () => {
  if (playerTabs.value.length === 0) return;
  isDownloading.value = true;
  showProgressModal.value = true;
  progressTitle.value = 'Creating Zip for All Players';
  progressPercentage.value = 0;
  cancelRequested.value = false;

  try {
    const zip = new JSZip();

    // Calculate total operations based on selected renders
    const selectedRenders = Object.entries(renderOptions.value.renders)
      .filter(([, render]) => render.selected)
      .map(([key]) => key);

    const totalOperationsPerPlayer = selectedRenders.length;
    const totalOperations = playerTabs.value.length * totalOperationsPerPlayer;
    let completedOperations = 0;

    // Create a folder for each player
    for (const player of playerTabs.value) {
      if (cancelRequested.value) return;

      const folder = zip.folder(player.username);

      // Download selected renders for each player
      for (const renderType of selectedRenders) {
        if (cancelRequested.value) return;

        progressPercentage.value = Math.round((completedOperations / totalOperations) * 80);

        let url;
        let filename;

        switch (renderType) {
          case 'faceFlat':
            url = `https://api.mineatar.io/face/${player.raw_id}?scale=100&overlay=true`;
            filename = `${player.username}_face_flat.png`;
            break;
          case 'face':
            url = `https://vzge.me/face/512/${player.raw_id}.png`;
            filename = `${player.username}_face.png`;
            break;
          case 'flatHead':
            url = `https://api.mineatar.io/head/${player.raw_id}?scale=100&overlay=true`;
            filename = `${player.username}_head_flat.png`;
            break;
          case 'head3d':
            url = `https://skins.mcstats.com/skull/${player.raw_id}`;
            filename = `${player.username}_head_3d.png`;
            break;
          case 'bust3d':
            url = `https://nmsr.nickac.dev/bust/${player.raw_id}`;
            filename = `${player.username}_bust_3d.png`;
            break;
          case 'bustFlat':
            url = `https://minotar.net/armor/bust/${player.raw_id}/1024.png`;
            filename = `${player.username}_bust_flat.png`;
            break;
          case 'fullBody':
            url = `https://api.mineatar.io/body/full/${player.raw_id}?scale=27&overlay=true`;
            filename = `${player.username}_full_body.png`;
            break;
          case 'frontBodyFlat':
            url = `https://api.mineatar.io/body/front/${player.raw_id}?scale=100&overlay=true`;
            filename = `${player.username}_front_body_flat.png`;
            break;
          case 'frontBody3d':
            url = `https://nmsr.nickac.dev/fullbody/${player.raw_id}`;
            filename = `${player.username}_front_body_3d.png`;
            break;
          case 'backBodyFlat':
            url = `https://api.mineatar.io/body/back/${player.raw_id}?scale=100&overlay=true`;
            filename = `${player.username}_back_body_flat.png`;
            break;
          case 'backBody3d':
            url = `https://skins.mcstats.com/body/back/${player.raw_id}`;
            filename = `${player.username}_back_body_3d.png`;
            break;
          case 'leftBodyFlat':
            url = `https://api.mineatar.io/body/left/${player.raw_id}?scale=100&overlay=true`;
            filename = `${player.username}_left_body_flat.png`;
            break;
          case 'rightBodyFlat':
            url = `https://api.mineatar.io/body/right/${player.raw_id}?scale=100&overlay=true`;
            filename = `${player.username}_right_body_flat.png`;
            break;
          case 'sideBody3d':
            url = `https://nmsr.nickac.dev/fullbodyiso/${player.raw_id}`;
            filename = `${player.username}_side_body_3d.png`;
            break;
          case 'rawSkin':
            url = `https://api.mineatar.io/skin/${player.raw_id}`;
            filename = `${player.username}_raw_skin.png`;
            break;
        }

        const response = await fetch(url);
        const blob = await response.blob();
        folder.file(filename, blob);
        completedOperations++;
      }
    }

    // Generate and download the zip
    progressPercentage.value = 80;

    const content = await zip.generateAsync({
      type: 'blob',
      comment: 'Created using Vault Skin Viewer, visit https://mcicons.ccleaf.com/ for more!'
    }, (metadata) => {
      progressPercentage.value = 80 + Math.round(metadata.percent / 5);
    });

    if (!cancelRequested.value) {
      progressPercentage.value = 100;
      await new Promise(resolve => setTimeout(resolve, 500));
      saveAs(content, `minecraft_skins_collection.zip`);
      successMessage.value = 'Zip file with all skins created and downloaded!';
      setTimeout(() => { successMessage.value = ''; }, 3000);
    }
  } catch (error) {
    console.error('Error creating zip file:', error);
    errorMessage.value = 'Failed to create zip file. Please try again.';
    setTimeout(() => { errorMessage.value = ''; }, 3000);
  } finally {
    if (!cancelRequested.value) {
      showProgressModal.value = false;
      isDownloading.value = false;
      progressPercentage.value = 0;
    }
    cancelRequested.value = false;
  }
};

// Download external image
const downloadExternalImage = async (url, type) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);

    // Set filename based on type and player name
    const playerName = activePlayer.value.username;
    const typeName = type.split('_').join(' ');
    link.download = `${playerName}_${typeName}.png`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  } catch (error) {
    console.error('Error downloading external image:', error);
    errorMessage.value = 'Failed to download external image. Please try again.';
    setTimeout(() => { errorMessage.value = ''; }, 3000);
  }
};

const clearAllTabs = () => {
  playerTabs.value = [];
  activeTabIndex.value = 0;
  savePlayers();
  showClearConfirmation.value = false;
};

// Function to show download options modal
const openDownloadOptions = (scope = 'current') => {
  // Reset selectAll to true if all options are selected
  renderOptions.value.selectAll = allRendersSelected.value;

  // Single tab always uses current scope
  downloadScope.value = playerTabs.value.length <= 1 ? 'current' : scope;

  showDownloadOptions.value = true;
};
</script>

<style scoped>
.mc-skins-container {
  color: #e0e0e0;
  width: 100%;
}

.mc-search-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 0.5rem;
  width: 100%;
}

.mc-search-input {
  display: flex;
  align-items: center;
  background-color: #0D0D0D;
  border: 2px solid #1D1D1D;
  border-radius: 20px;
  flex: 1;
  padding: 0.5rem;
  transition: all 0.3s ease;
}

.mc-search-input input {
  color: #ffffff;
  flex: 1;
}

.mc-search-input:hover {
  border: 2px solid #A4A7B7;
  background: #45484C;
}

.mc-search-input:focus-within {
  border: 2px solid #4de8ff;
  background: #1a2d44;
}

.search-button {
  position: relative;
  background: linear-gradient(135deg, #00e5ff 0%, #00b8d4 100%);
  color: #001018;
  border: none;
  border-radius: 999px;
  padding: 0.45rem 1.15rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  text-transform: capitalize;
  font-size: 0.8rem;
  min-width: 72px;
  white-space: nowrap;
  box-shadow: 0 2px 10px rgba(0, 229, 255, 0.25),
              inset 0 1px 0 rgba(255, 255, 255, 0.25);
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1),
              box-shadow 0.25s ease,
              background 0.25s ease,
              filter 0.2s ease;
  overflow: hidden;
}

.search-button::after {
  content: '';
  position: absolute;
  top: 0;
  left: -75%;
  width: 50%;
  height: 100%;
  background: linear-gradient(120deg,
    transparent 0%,
    rgba(255, 255, 255, 0.35) 50%,
    transparent 100%);
  transform: skewX(-20deg);
  transition: left 0.6s ease;
  pointer-events: none;
}

.search-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #3aefff 0%, #00d1ee 100%);
  transform: translateY(-1px) scale(1.03);
  box-shadow: 0 6px 18px rgba(0, 229, 255, 0.45),
              inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.search-button:hover:not(:disabled)::after {
  left: 125%;
}

.search-button:active:not(:disabled) {
  transform: translateY(0) scale(0.98);
  box-shadow: 0 2px 8px rgba(0, 229, 255, 0.35),
              inset 0 2px 4px rgba(0, 0, 0, 0.15);
}

.search-button:disabled {
  background: linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%);
  color: #777;
  cursor: not-allowed;
  box-shadow: none;
  filter: grayscale(0.4);
}

.banner-container2 {
  display: flex;
  align-items: center;
  height: 60px;
  justify-content: flex-end;
}

.promo-banner {
  height: 100%;
  width: auto;
  max-width: 100%;
  transition: transform 0.2s;
}

.promo-banner:hover {
  transform: scale(1.05);
}

/* Alert messages */
.mc-alert {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 1000;
  padding: 0.75rem 1rem 0.75rem 1.25rem;
  margin-bottom: 0;
  border-radius: 12px;
  font-size: 0.9rem;
  background-color: #0D0D0D;
  border: 2px solid #1D1D1D;
  min-width: 18rem;
  max-width: calc(100% - 2rem);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  animation: alertSlideUpIn 0.3s ease-out forwards;
  overflow: hidden;
}

.mc-alert::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 4px;
}

.mc-alert-error {
  color: #ff5555;
}

.mc-alert-error::before {
  background-color: #ff5555;
}

.mc-alert-success {
  color: #00e5ff;
}

.mc-alert-success::before {
  background-color: #00e5ff;
}

@keyframes alertSlideUpIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Tabs navigation */
.mc-tabs-navigation-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.mc-clear-all-tabs {
  background-color: #1e1e1e;
  color: #ff3a3a;
  border: 2px solid #ff3a3a;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  flex-shrink: 0;
  transition: all 0.2s ease;
  margin-left: auto;
  padding: 0;
}

.mc-clear-all-tabs:hover {
  background-color: #2a2a2a;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 58, 58, 0.35);
}

.trash-icon {
  width: 20px;
  height: 20px;
  stroke: currentColor;
}

.mc-tabs-container {
  overflow-x: auto;
  scroll-behavior: smooth;
  width: 100%;
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE and Edge */
}

.mc-tabs-container::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari, Opera */
}

.mc-tabs-scroll-left,
.mc-tabs-scroll-right {
  background-color: #1e1e1e;
  color: #00e5ff;
  border: 2px solid #00e5ff;
  width: 30px;
  height: 40px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  flex-shrink: 0;
  transition: all 0.2s ease;
  font-weight: bold;
  font-size: 18px;
  line-height: 1;
  margin-top: -5px;
}

.mc-tabs-scroll-left {
  margin-right: 5px;
}

.mc-tabs-scroll-right {
  margin-left: 5px;
}

.mc-tabs-scroll-left:hover,
.mc-tabs-scroll-right:hover {
  background-color: #2a2a2a;
  color: #00e5ff;
}

/* Player content */
.mc-skins-content {
  background-color: #0D0D0D;
  border: 2px solid #1D1D1D;
  border-radius: 20px;
  padding: 1rem;
  margin-top: 0.5rem;
}

.mc-player-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.mc-player-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.mc-player-avatar {
  width: 48px;
  height: 48px;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

.mc-player-username {
  font-size: 1.25rem;
  font-weight: bold;
  color: #00e5ff;
  margin: 0;
  margin-bottom: 0.25rem;
}

.mc-player-uuid {
  font-size: 0.8rem;
  color: #a0a0a0;
  font-family: monospace;
  margin: 0;
}

.mc-download-all-btn,
.mc-download-all-tabs-btn {
  background-color: #00e5ff;
  color: #000000;
  border: 2px solid transparent;
  padding: 0.5rem 1.1rem;
  border-radius: 999px;
  font-weight: bold;
  cursor: pointer;
  text-transform: capitalize;
  transition: all 0.2s ease;
}

.mc-download-all-btn:hover:not(:disabled),
.mc-download-all-tabs-btn:hover:not(:disabled) {
  background-color: #00b8d4;
  transform: translateY(-2px);
}

.mc-download-all-btn:disabled,
.mc-download-all-tabs-btn:disabled {
  background-color: #cccccc;
  color: #666666;
  cursor: not-allowed;
}

.mc-download-player-btn {
  background-color: transparent;
  color: #00e5ff;
  border-color: #00e5ff;
}

.mc-download-player-btn:hover:not(:disabled) {
  background-color: rgba(0, 229, 255, 0.12);
  color: #00e5ff;
}

.mc-download-player-btn:disabled {
  background-color: transparent;
  border-color: #555;
  color: #777;
}

.mc-player-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* Skins grid */
.mc-skins-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.mc-skin-item {
  background-color: #2a2a2a;
  border: 2px solid #333;
  overflow: hidden;
  transition: all 0.2s ease;
}

.mc-skin-item:hover {
  background-color: #1a2d44;
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  border-color: #00e5ff;
}

.mc-skin-preview {
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000000;
  padding: 1rem;
  position: relative;

  /* Create checkered background */
  background: linear-gradient(45deg, rgba(0, 0, 0, 0.1) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.1) 75%, rgba(0, 0, 0, 0.1) 0),
    linear-gradient(45deg, rgba(0, 0, 0, 0.1) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.1) 75%, rgba(0, 0, 0, 0.1) 0),
    #1e1e1e;
  background-repeat: repeat, repeat;
  background-position: 0px 0, 10px 10px;
  background-size: 20px 20px, 20px 20px;
}

.mc-skin-preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

.mc-raw-skin {
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  width: 128px;
  height: 128px;
}

.mc-skin-info {
  padding: 0.75rem;
  position: relative;
  height: 80px;
  /* Fixed height to prevent layout shift */
}

.mc-skin-info h3 {
  margin: 0;
  font-size: 0.9rem;
  color: #ffffff;
  text-align: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transition: all 0.3s ease;
  opacity: 1;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 1rem;
  box-sizing: border-box;
}

.mc-skin-options {
  display: flex;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  opacity: 0;
  transition: all 0.3s ease;
  width: 80%;
}

.mc-skin-item:hover .mc-skin-info h3 {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.8);
}

.mc-skin-item:hover .mc-skin-options {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

.mc-download-btn {
  transition: all 0.2s ease;
  width: 100%;
  margin: .5rem;
  background: #0D0D0D;
  color: #00e5ff;
  border: 2px solid #1D1D1D;
  border-radius: 20px;
  padding: .75rem;
  cursor: pointer;
  font-size: small;
  text-transform: capitalize;
  transition: all .2s ease;
}

.mc-download-btn:hover {
  transform: translateY(-2px);
  background: #00e5ff;
  color: #000000;
}

.mc-download-all-tabs {
  margin-top: 1.5rem;
  text-align: center;
}

/* Empty state */
.mc-empty-state {
  text-align: center;
  background-color: #0D0D0D;
  border: 2px solid #1D1D1D;
  border-radius: 20px;
  padding: 1rem;
  min-height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.mc-empty-state-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #e0e0e0;
  margin-bottom: 1rem;
}

.mc-empty-state-message {
  color: #a0a0a0;
  font-size: smaller;
  max-width: 80%;
  margin: 0 auto;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .mc-search-container {
    flex-direction: column;
    gap: 0.75rem;
  }

  .banner-container2 {
    max-width: 100%;
    justify-content: center;
  }

  .mc-player-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .mc-skins-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }

  .mc-skin-preview {
    height: 160px;
  }
}

/* Download Progress Modal */
.mc-progress-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.mc-progress-modal {
  background-color: #1e1e1e;
  border: 2px solid #333;
  width: 90%;
  max-width: 500px;
  border-radius: 0;
}

.mc-progress-modal-header {
  padding: 1rem;
  border-bottom: 1px solid #333;
}

.mc-progress-modal-header h3 {
  margin: 0;
  color: #00e5ff;
  font-size: 1.2rem;
}

.mc-progress-modal-content {
  padding: 1.5rem;
}

.mc-progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.mc-progress-percentage {
  font-size: 1.5rem;
  font-weight: bold;
  color: #00e5ff;
}

.mc-progress-bar-container {
  width: 100%;
  height: 20px;
  background-color: #333;
  margin-bottom: 1.5rem;
  overflow: hidden;
  border: 1px solid #444;
}

.mc-progress-bar {
  height: 100%;
  background-color: #00e5ff;
  transition: width 0.3s ease;
}

.mc-cancel-button {
  background-color: #ff3a3a;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: bold;
  text-transform: capitalize;
  width: 100%;
  transition: all 0.2s ease;
}

.mc-cancel-button:hover {
  background-color: #ff5555;
  transform: translateY(-2px);
}

.mc-tabs-navigation {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.25rem;
  padding-bottom: 0.25rem;
  min-width: min-content;
}

.mc-tab-item {
  padding: .35rem .5rem .35rem .5rem;
  background-color: #0D0D0D;
  border: 2px solid #1D1D1D;
  border-radius: 20px;
  color: #c7c7c8;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.mc-tab-item:hover {
  color: #fff;
  background-color: #1a1a1a;
  border-color: #2a2a2a;
}

.mc-tab-active {
  background-color: #0D0D0D;
  border: 2px solid #00e5ff;
  border-radius: 20px;
  color: #00e5ff;
  box-shadow: 0 0 0 3px rgba(0, 229, 255, 0.15);
}

.mc-tab-active:hover {
  background-color: #0D0D0D;
  border-color: #00e5ff;
}

.mc-tab-face {
  width: 22px;
  height: 22px;
  image-rendering: pixelated;
  border-radius: 4px;
  flex-shrink: 0;
}

.mc-tab-username {
  font-size: 0.9rem;
}

.mc-tab-close {
  background: none;
  border: none;
  color: #c7c7c8;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;
  padding: 0;
  line-height: 1;
}

.mc-tab-close:hover {
  color: #ff5555;
}

/* Dropdown menu */
.mc-dropdown {
  position: relative;
  display: inline-block;
}

.mc-dropdown-content {
  display: none;
  position: absolute;
  right: 0;
  background-color: #1e1e1e;
  min-width: 160px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.5);
  z-index: 10;
}

.mc-dropdown-item {
  color: #e0e0e0;
  padding: 8px 16px;
  text-decoration: none;
  display: block;
  background-color: transparent;
  border: none;
  text-align: left;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: smaller;
}

.mc-dropdown-item:hover {
  background-color: #2a2a2a;
  color: #00e5ff;
}

.mc-dropdown:hover .mc-dropdown-content {
  display: block;
}

/* Credits section */
.mc-credits {
  margin-top: 1rem;
  text-align: center;
  font-size: 0.8rem;
  color: #929292;
  padding: 0.75rem;
}

.mc-credits a {
  color: #ccc;
  text-decoration: none;
  transition: all 0.2s ease;
}

.mc-credits a:hover {
  color: #ffffff;
  text-decoration: underline;
}

/* Modal Animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.3s ease;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Clear All Confirmation Modal */
.mc-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.mc-confirm-modal {
  background-color: #1e1e1e;
  border: 2px solid #333;
  width: 90%;
  max-width: 500px;
  border-radius: 0;
}

.mc-confirm-modal-header {
  padding: 1rem;
  border-bottom: 1px solid #333;
}

.mc-confirm-modal-header h3 {
  margin: 0;
  color: #00e5ff;
  font-size: 1.2rem;
}

.mc-confirm-modal-content {
  padding: 1.5rem;
}

.mc-confirm-modal-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
}

.mc-cancel-modal-btn {
  background-color: #ff3a3a;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: bold;
  text-transform: capitalize;
  width: 48%;
  transition: all 0.2s ease;
}

.mc-cancel-modal-btn:hover {
  background-color: #ff5555;
  transform: translateY(-2px);
}

.mc-confirm-modal-btn {
  background-color: #00e5ff;
  color: #000000;
  border: none;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: bold;
  text-transform: capitalize;
  width: 48%;
  transition: all 0.2s ease;
}

.mc-confirm-modal-btn:hover {
  background-color: #00b8d4;
  transform: translateY(-2px);
}

/* Download Options Modal Styles */
.mc-download-options-modal {
  background-color: #1e1e1e;
  border: 2px solid #333;
  width: 90%;
  max-width: 550px;
  border-radius: 0;
}

.mc-download-options-header {
  padding: 1rem;
  border-bottom: 1px solid #333;
}

.mc-download-options-header h3 {
  margin: 0;
  color: #00e5ff;
  font-size: 1.2rem;
}

.mc-download-options-content {
  padding: 0.5rem 1rem;
  max-height: 60vh;
  overflow-y: auto;
}

.mc-download-scope {
  display: flex;
  gap: 1.5rem;
  margin: 1rem 0.5rem;
}

.mc-radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #e0e0e0;
  cursor: pointer;
  position: relative;
  padding-left: 30px;
}

.mc-radio-label input[type="radio"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.radio-custom {
  position: absolute;
  left: 0;
  height: 20px;
  width: 20px;
  background-color: #2a2a2a;
  border: 2px solid #444;
  border-radius: 50%;
}

.mc-radio-label:hover input~.radio-custom {
  background-color: #373737;
}

.mc-radio-label input:checked~.radio-custom {
  border-color: #00e5ff;
}

.radio-custom:after {
  content: "";
  position: absolute;
  display: none;
  top: 50%;
  left: 50%;
  width: 10px;
  height: 10px;
  background: #00e5ff;
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.mc-radio-label input:checked~.radio-custom:after {
  display: block;
}

.mc-render-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0;
}

.mc-render-options.no-scope {
  margin-top: 0.5rem;
}

.mc-checkbox-label {
  display: flex;
  align-items: center;
  color: #e0e0e0;
  cursor: pointer;
  padding: 0.5rem;
  transition: all 0.2s ease;
  position: relative;
  padding-left: 40px;
}

.select-all-checkbox {
  border-bottom: 1px solid #333;
}

.mc-checkbox-label:hover {
  background-color: #2a2a2a;
}

.mc-checkbox-label input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.checkbox-custom {
  position: absolute;
  left: 10px;
  height: 20px;
  width: 20px;
  background-color: #2a2a2a;
  border: 2px solid #444;
}

.mc-checkbox-label:hover input~.checkbox-custom {
  background-color: #373737;
}

.mc-checkbox-label input:checked~.checkbox-custom {
  border-color: #00e5ff;
}

.checkbox-custom:after {
  content: "";
  position: absolute;
  display: none;
  left: 7px;
  top: 3px;
  width: 4px;
  height: 8px;
  border: solid #00e5ff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.mc-checkbox-label input:checked~.checkbox-custom:after {
  display: block;
}

.mc-render-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.mc-download-options-actions {
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  border-top: 1px solid #333;
}

.mc-download-options-actions button {
  width: 48%;
}

.mc-download-options-actions button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>